import {
  formatMoney,
  formatPercent,
  magnitude,
} from '@/features/workspace-usage';
import { PeriodType } from '@/lib/api/generated/model';

/** Shared by every limit-editing dialog's live summary sentence and "follows X" copy. */
export const PERIOD_NOUN: Record<PeriodType, string> = {
  [PeriodType.daily]: 'day',
  [PeriodType.weekly]: 'week',
  [PeriodType.monthly]: 'month',
  [PeriodType.none]: 'lifetime',
};

/**
 * The live summary sentence beneath a period control (spec 091, `.local/set-ws-budget-dlg.png`).
 *
 * Shared by `OverrideDialog` (a workspace's own limit) and `WorkflowLimitDialog` (a
 * workflow's limit stacked inside one) — both name a subject, an amount, a cadence, and
 * how much of the current period has already been spent, and neither should invent its
 * own wording for the same sentence shape.
 */
export function budgetSummarySentence({
  name,
  amount,
  periodType,
  spend,
  currency,
}: {
  name: string;
  amount: string;
  periodType: PeriodType;
  spend: string;
  currency: string;
}) {
  const limit = magnitude(amount);
  const percent = limit > 0 ? formatPercent(magnitude(spend) / limit) : null;
  const spentClause =
    percent === null
      ? null
      : periodType === PeriodType.none
        ? ` It has spent ${formatMoney(spend, currency)} of its lifetime cap — ${percent}.`
        : ` It has spent ${formatMoney(spend, currency)} of the current period — ${percent}.`;

  const cadence =
    periodType === PeriodType.none
      ? ', a lifetime cap'
      : ` each ${PERIOD_NOUN[periodType]}`;

  return (
    <>
      <span className="font-medium">{name}</span> gets{' '}
      <span className="font-medium">{formatMoney(amount, currency)}</span>
      {cadence}.{spentClause}
    </>
  );
}
